import React, { useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { endOfMonth, endOfYear, format, startOfMonth, startOfYear } from "date-fns";
import { economyApi } from "../../../../api/economy";
import { CashMovement } from "./CashMovement";
import { Budgets } from "./Budgets";
import { Liquidity } from "./Liquidity";
import { Operating } from "./Operating";
import { Payroll } from "./Payroll";
import { Vehicles } from "./Vehicles";
import { Button, ErrorBox, useEconomyText, useRemote } from "./shared";
import { PeriodSelector } from "../../hours/period_selector";
import { useUrlTab } from "../../../utils/useUrlTab";
import { useSettings } from "../../../../contexts/settings-context";
import "./economy.css";

const TAB_KEYS = ["payroll", "operating", "vehicles", "budgets", "liquidity"];
const TABS = TAB_KEYS.map((tabKey) => ({ tabKey }));
const ESTIMATE_TABS = new Set(["payroll", "vehicles"]);
const PERIOD_TABS = new Set(["payroll", "operating", "vehicles"]);

function monthPeriod(date) {
  return {
    periodType: "month",
    from: format(startOfMonth(date), "yyyy-MM-dd"),
    to: format(endOfMonth(date), "yyyy-MM-dd"),
  };
}

function economyParamsFromPeriod(period) {
  const date = period?.from ? new Date(`${period.from}T00:00:00`) : new Date();
  const year = date.getFullYear();
  const throughMonth = period?.periodType === "year" ? 12 : date.getMonth() + 1;
  return { year, through_month: throughMonth };
}

export default function EconomyWorkspace({ children }) {
  const companyId = useSelector((state) => state.session.currentCompany?.id);
  return companyId ? <CompanyEconomy key={companyId}>{children}</CompanyEconomy> : <EconomyShell>{children}</EconomyShell>;
}
function CompanyEconomy({ children }) {
  const { t } = useEconomyText();
  const [period, setPeriod] = useState(() => monthPeriod(new Date()));
  const [selectedTab, selectTab] = useUrlTab(TABS);
  const tab = TABS[selectedTab]?.tabKey || "payroll";
  const [budgetRevision, setBudgetRevision] = useState(0);
  const [payrollRevision, setPayrollRevision] = useState(0);
  const [budgetEditing, setBudgetEditing] = useState(false);
  const [payrollEditing, setPayrollEditing] = useState(false);
  const [vehicleEditing, setVehicleEditing] = useState(false);
  const [operatingEditing, setOperatingEditing] = useState(false);
  const [selectedBudgetId, setSelectedBudgetId] = useState("");
  const editing = operatingEditing || budgetEditing || payrollEditing || vehicleEditing;
  const tabRefs = useRef([]);
  const { year, through_month: month } = useMemo(() => economyParamsFromPeriod(period), [period]);
  const params = { year, through_month: month };
  const needsEstimates = ESTIMATE_TABS.has(tab);
  const source = useRemote((signal) => economyApi.sources(params, signal), [year, month]);
  const estimates = useRemote(
    (signal) => (needsEstimates ? economyApi.estimates(params, signal) : Promise.resolve({ data: null })),
    [year, month, needsEstimates],
  );
  const onTabKey = (event, index) => {
    let next;
    if (event.key === "ArrowRight") next = (index + 1) % TAB_KEYS.length;
    if (event.key === "ArrowLeft") next = (index + TAB_KEYS.length - 1) % TAB_KEYS.length;
    if (event.key === "Home") next = 0;
    if (event.key === "End") next = TAB_KEYS.length - 1;
    if (next != null) { event.preventDefault(); tabRefs.current[next]?.focus(); selectTab(next); }
  };
  const onPeriodChange = ({ periodType, from }) => {
    if (periodType === "year") {
      const date = from ? new Date(`${from}T00:00:00`) : new Date();
      setPeriod({
        periodType: "year",
        from: format(startOfYear(date), "yyyy-MM-dd"),
        to: format(endOfYear(date), "yyyy-MM-dd"),
      });
      return;
    }
    const date = from ? new Date(`${from}T00:00:00`) : new Date();
    setPeriod(monthPeriod(date));
  };
  const periodControls = <div className="eco-period">
    <fieldset disabled={editing} className="min-w-0 border-0 p-0">
      <PeriodSelector
        periodType={period.periodType}
        from={period.from}
        to={period.to}
        allowedPeriodTypes={["year", "month"]}
        onChange={onPeriodChange}
      />
    </fieldset>
  </div>;
  return <EconomyShell source={source.data} print={<PrintOverviewButton year={year} month={month} budgetId={selectedBudgetId} />}>
    <CashMovement source={source.data} loading={source.loading} failed={Boolean(source.error)} />
    <nav className="eco-tabs" role="tablist" aria-label={t("title")}>{TAB_KEYS.map((key, index) => <button ref={(element) => { tabRefs.current[index] = element; }} key={key} role="tab" id={`eco-tab-${key}`} aria-controls={`eco-panel-${key}`} aria-selected={tab === key} tabIndex={tab === key ? 0 : -1} onKeyDown={(event) => onTabKey(event, index)} onClick={() => selectTab(index)}><span>0{index + 1}</span>{t(key)}{key === "budgets" ? ` ${year + 1}` : ""}</button>)}</nav>
    {!source.data && PERIOD_TABS.has(tab) && periodControls}
    <ErrorBox error={source.error} onRetry={source.refresh} />
    {source.loading && <div className="eco-loading" role="status"><span className="pm-btn__spinner" aria-hidden="true" />{t("loading")}</div>}
    {source.data && <div role="tabpanel" id={`eco-panel-${tab}`} aria-labelledby={`eco-tab-${tab}`}>
      {tab === "payroll" && <><ErrorBox error={estimates.error} onRetry={estimates.refresh} /><Payroll controls={periodControls} onEditingChange={setPayrollEditing} source={source.data} estimate={estimates.data?.payroll} loading={estimates.loading} refresh={() => { estimates.refresh(); setPayrollRevision((value) => value + 1); }} /></>}
      {tab === "operating" && <Operating controls={periodControls} onEditingChange={setOperatingEditing} source={source.data} />}
      {tab === "vehicles" && <><ErrorBox error={estimates.error} onRetry={estimates.refresh} /><Vehicles controls={periodControls} onEditingChange={setVehicleEditing} vehicles={estimates.data?.vehicles || source.data.vehicles} year={year} month={month} refresh={estimates.refresh} /></>}
      {tab === "budgets" && <Budgets payrollRevision={payrollRevision} onChanged={() => setBudgetRevision((value) => value + 1)} onSelect={setSelectedBudgetId} onEditingChange={setBudgetEditing} source={source.data} />}
      {tab === "liquidity" && <Liquidity payrollRevision={payrollRevision} budgetRevision={budgetRevision} source={source.data} />}
    </div>}
    {children}
  </EconomyShell>;
}

function EconomyShell({ source, children, print }) {
  const { t } = useEconomyText();
  const { settings } = useSettings();
  const theme = settings.theme === "dark" ? "dark" : "light";
  const updated = source?.generated_at
    ? new Date(source.generated_at).toLocaleDateString("nb-NO", { day: "2-digit", month: "2-digit", year: "numeric" })
    : null;
  const meta = source
    ? `${t("sourceUpdated")}${updated ? ` ${updated}` : ""} · ${source.employees.length} ${t("employees")} · ${source.vehicles.length} ${t("vehicles")}`
    : t("source");
  return <div className="economy" data-theme={theme}><div className="eco-content">
    <Helmet><link href="https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@400;500;600;700&family=DM+Sans:wght@400;500;600;700&family=Space+Mono:wght@400;700&display=swap" rel="stylesheet" /></Helmet>
    <header className="eco-heading"><div><span className="eco-label eco-eyebrow">{"// "}{t("eyebrow")}</span><h1>{t("title")}</h1></div>
      <div className="eco-heading-meta"><span className="eco-label">{meta}</span>{print}</div>
    </header>
    {children}
  </div></div>;
}

function PrintOverviewButton({ year, month, budgetId }) {
  const { t } = useEconomyText();
  const { i18n } = useTranslation();
  const [printing, setPrinting] = useState(false);
  const [failed, setFailed] = useState(false);
  const print = async () => {
    if (printing) return;
    setPrinting(true);
    setFailed(false);
    try {
      const response = await economyApi.overviewPdf({
        year,
        through_month: month,
        locale: i18n.language,
        ...(budgetId ? { budget_id: budgetId } : {}),
      });
      const blob = response?.data;
      if (!(blob instanceof Blob)) throw new Error("Invalid pdf");
      const header = new Uint8Array(await blob.slice(0, 4).arrayBuffer());
      if (String.fromCharCode(...header) !== "%PDF") throw new Error("Invalid pdf");
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = `okonomi-oversikt-${year}-${String(month).padStart(2, "0")}.pdf`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      // Revoking synchronously can cancel the download in some browsers.
      setTimeout(() => URL.revokeObjectURL(url), 0);
    } catch (_error) {
      setFailed(true);
    } finally {
      setPrinting(false);
    }
  };
  return <>
    <Button onClick={print} disabled={printing}>{printing ? t("printing") : t("printPdf")}</Button>
    {failed && <p className="eco-error" role="alert">{t("printFailed")}</p>}
  </>;
}
